import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { format, parseISO, differenceInDays } from "date-fns";
import {
  Package,
  Loader2,
  Search,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  User,
  Mail,
  Phone,
  CreditCard,
  RefreshCw,
  Plus,
  ChevronLeft,
  ChevronRight,
  QrCode
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPackageManagement = () => {
  const [packages, setPackages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modal states
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [addCreditsDialogOpen, setAddCreditsDialogOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [creditsToAdd, setCreditsToAdd] = useState(1);
  const [creditInputValue, setCreditInputValue] = useState("1");
  const [submitting, setSubmitting] = useState(false);

  const CREDIT_QUICK_OPTIONS = [-10, -5, -3, -1, 0, 1, 3, 5, 10, 20];

  useEffect(() => {
    fetchPackages();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page]);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/package-management?page=${page}&limit=15`;

      if (activeTab === "active") {
        url += "&status=active";
      } else if (activeTab === "expiring") {
        url += "&expiring_soon=true";
      } else if (activeTab === "expired") {
        url += "&status=expired";
      } else if (activeTab === "revenue") {
        url += "&payment_status=paid";
      }

      const res = await axios.get(url);
      setPackages(res.data.purchases || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Failed to load package data");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/package-management/stats`);
      setStats(res.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleExtend = async () => {
    if (!selectedPurchase) return;
    const days = Number(extendDays);
    if (!Number.isInteger(days) || days < 1) {
      toast.error("Enter at least 1 day to extend");
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/package-management/${selectedPurchase.id}/extend?days=${days}`);
      toast.success(`Package extended by ${days} days`);
      setExtendDialogOpen(false);
      fetchPackages();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extend package");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCredits = async () => {
    if (!selectedPurchase) return;
    const num = parseInt(creditInputValue, 10);
    if (Number.isNaN(num) || num < -50 || num > 50) {
      toast.error("Enter a number between -50 and 50");
      return;
    }
    if (num === 0) {
      toast.error("Enter a non-zero value to add or reduce credits");
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/package-management/${selectedPurchase.id}/add-credits?credits=${num}`);
      if (num > 0) {
        toast.success(`Added ${num} credits`);
      } else {
        toast.success(`Reduced credits by ${Math.abs(num)}`);
      }
      setAddCreditsDialogOpen(false);
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update credits");
    } finally {
      setSubmitting(false);
    }
  };

  const openExtendDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setExtendDays(30);
    setExtendDialogOpen(true);
  };

  const openAddCreditsDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setCreditsToAdd(1);
    setCreditInputValue("1");
    setAddCreditsDialogOpen(true);
  };

  const getExpiryStatusBadge = (purchase) => {
    if (purchase.is_expired) {
      return <Badge className="bg-red-500/20 text-red-400">Expired</Badge>;
    }

    if (purchase.is_expiring_soon) {
      const days = purchase.days_until_expiry;
      const credits = purchase.credits_remaining;
      return (
        <Badge className="bg-orange-500/20 text-orange-400 animate-pulse">
          Expiring soon
          {days !== null && days >= 0 && (
            <span className="ml-1 text-xs">({days} days)</span>
          )}
          {credits !== undefined && (
            <span className="ml-1 text-xs">({credits} credits left)</span>
          )}
        </Badge>
      );
    }

    if (purchase.days_until_expiry !== null) {
      return (
        <Badge className="bg-green-500/20 text-green-400">
          {purchase.days_until_expiry} days left
        </Badge>
      );
    }

    return <Badge className="bg-neutral-500/20 text-neutral-400">No expiry</Badge>;
  };

  const filteredPackages = packages.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      p.guest_name?.toLowerCase().includes(term) ||
      p.guest_email?.toLowerCase().includes(term) ||
      p.guest_phone?.includes(term) ||
      p.package_name?.toLowerCase().includes(term) ||
      p.purchase_number?.toLowerCase().includes(term)
    );
  });

  return (
    <div data-testid="admin-package-management">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Package Management</h1>
        <p className="text-neutral-400">Track package expiry, credits, and client subscriptions</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
          <Card
            className="card-empire cursor-pointer"
            onClick={() => {
              setActiveTab("all");
              setPage(1);
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white">{stats.total_purchases}</h3>
              <p className="text-sm text-neutral-400">Total Purchases</p>
            </CardContent>
          </Card>

          <Card
            className="card-empire cursor-pointer"
            onClick={() => {
              setActiveTab("active");
              setPage(1);
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-green-400">{stats.active_packages}</h3>
              <p className="text-sm text-neutral-400">Active</p>
            </CardContent>
          </Card>

          <Card
            className="card-empire cursor-pointer"
            onClick={() => {
              setActiveTab("expiring");
              setPage(1);
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-orange-400">{stats.expiring_soon}</h3>
              <p className="text-sm text-neutral-400">Expiring Soon</p>
            </CardContent>
          </Card>

          <Card
            className="card-empire cursor-pointer"
            onClick={() => {
              setActiveTab("expired");
              setPage(1);
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-red-400">{stats.expired_packages}</h3>
              <p className="text-sm text-neutral-400">Expired</p>
            </CardContent>
          </Card>

          <Card
            className="card-empire cursor-pointer"
            onClick={() => {
              setActiveTab("revenue");
              setPage(1);
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-xl bg-empire-gold/20 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-empire-gold" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-empire-gold">KES {stats.total_revenue?.toLocaleString()}</h3>
              <p className="text-sm text-neutral-400">Revenue</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, phone, or package..."
            className="input-empire pl-10"
          />
        </div>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(1); }}>
          <TabsList className="bg-neutral-900 border border-neutral-800">
            <TabsTrigger value="all" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
              All
            </TabsTrigger>
            <TabsTrigger value="active" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
              Active
            </TabsTrigger>
            <TabsTrigger value="expiring" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
              Expiring Soon
            </TabsTrigger>
            <TabsTrigger value="expired" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
              Expired
            </TabsTrigger>
            <TabsTrigger value="revenue" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
              Revenue
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Revenue tab: show total revenue header */}
      {activeTab === "revenue" && stats && (
        <Card className="card-empire mb-6 border-empire-gold/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-sm font-medium text-neutral-400">Total revenue (paid package transactions)</h3>
                <p className="text-3xl font-bold text-empire-gold mt-1">KES {(stats.total_revenue ?? 0).toLocaleString()}</p>
              </div>
              <div className="text-sm text-neutral-500">
                {filteredPackages.length === 0 && !loading
                  ? "No paid transactions to list. Mark package payments as paid to see them below."
                  : "Paid transactions (receipts) below."}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Package List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
        </div>
      ) : filteredPackages.length === 0 ? (
        <Card className="card-empire">
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
            <p className="text-neutral-500">
              {activeTab === "revenue"
                ? "No paid transactions yet. Total revenue is shown above when payments are marked as paid."
                : "No packages found"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredPackages.map((purchase, index) => (
            <motion.div
              key={purchase.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className={`card-empire ${purchase.is_expired ? "opacity-70" : ""}`}>
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Client Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-12 h-12 rounded-full bg-empire-gold/20 flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-empire-gold" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-white text-lg truncate">{purchase.guest_name}</h3>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-400">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {purchase.guest_email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {purchase.guest_phone}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Package Details */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <span className="text-neutral-500 block">Package</span>
                          <span className="text-white font-medium">{purchase.package_name}</span>
                        </div>
                        <div>
                          <span className="text-neutral-500 block">Price</span>
                          <span className="text-empire-gold font-bold">
                            KES {purchase.package_price?.toLocaleString()}
                            {purchase.discount_applied > 0 && (
                              <span className="text-green-400 text-xs ml-1">
                                (-{purchase.discount_applied.toLocaleString()})
                              </span>
                            )}
                          </span>
                        </div>
                        <div>
                          <span className="text-neutral-500 block">Purchased</span>
                          <span className="text-neutral-300">
                            {purchase.purchased_at && format(parseISO(purchase.purchased_at), "MMM d, yyyy")}
                          </span>
                        </div>
                        <div>
                          <span className="text-neutral-500 block">Expires</span>
                          <span className="text-neutral-300">
                            {purchase.expires_at ? format(parseISO(purchase.expires_at), "MMM d, yyyy") : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Credits & Status */}
                    <div className="flex flex-col sm:flex-row lg:flex-col items-start sm:items-center lg:items-end gap-3">
                      {/* Credits Display */}
                      <div className="bg-neutral-800/50 rounded-xl p-4 text-center min-w-[120px]">
                        <div className="text-3xl font-bold text-white">
                          {purchase.credits_remaining}
                          <span className="text-lg text-neutral-500">/{purchase.credits_purchased}</span>
                        </div>
                        <div className="text-xs text-neutral-400">Credits Remaining</div>
                        <div className="w-full bg-neutral-700 rounded-full h-2 mt-2">
                          <div
                            className="bg-empire-gold h-2 rounded-full transition-all"
                            style={{ width: `${(purchase.credits_remaining / purchase.credits_purchased) * 100}%` }}
                          />
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="flex flex-col gap-2">
                        {getExpiryStatusBadge(purchase)}
                        <Badge className={`${purchase.payment_status === "paid"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-yellow-500/20 text-yellow-400"
                          }`}>
                          {purchase.payment_status}
                        </Badge>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-row lg:flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openExtendDialog(purchase)}
                        className="border-neutral-700 hover:bg-neutral-800 text-xs"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Extend
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openAddCreditsDialog(purchase)}
                        className="border-neutral-700 hover:bg-neutral-800 text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Credits
                      </Button>
                      {purchase.qr_code_hash && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-empire-gold/50 text-empire-gold hover:bg-empire-gold/20 text-xs"
                          onClick={() => {
                            navigator.clipboard.writeText(purchase.qr_code_hash);
                            toast.success("QR hash copied!");
                          }}
                        >
                          <QrCode className="w-3 h-3 mr-1" />
                          QR Hash
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-neutral-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-neutral-400">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="border-neutral-700"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Extend Package Dialog */}
      <Dialog open={extendDialogOpen} onOpenChange={setExtendDialogOpen}>
        <DialogContent className="bg-neutral-900 border-neutral-800">
          <DialogHeader>
            <DialogTitle className="text-white">Extend Package</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedPurchase && (
              <div className="bg-neutral-800/50 rounded-lg p-4">
                <p className="text-white font-medium">{selectedPurchase.guest_name}</p>
                <p className="text-neutral-400 text-sm">{selectedPurchase.package_name}</p>
                <p className="text-neutral-500 text-sm">
                  Current expiry: {selectedPurchase.expires_at ? format(parseISO(selectedPurchase.expires_at), "MMM d, yyyy") : "N/A"}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-white">Extend by (days)</Label>
              <Input
                type="number"
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value === "" ? 0 : Number(e.target.value))}
                className="input-empire"
                min={0}
                max={365}
                placeholder="e.g. 30"
              />
              <p className="text-xs text-neutral-500">Enter 1 or more days to extend. 0 = no change.</p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setExtendDialogOpen(false)}
                className="flex-1 border-neutral-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleExtend}
                disabled={submitting || extendDays < 1}
                className="flex-1 btn-empire"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Extend Package"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Credits Dialog */}
      <Dialog open={addCreditsDialogOpen} onOpenChange={setAddCreditsDialogOpen}>
        <DialogContent className="bg-neutral-900 border-neutral-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add or reduce credits</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedPurchase && (
              <div className="bg-neutral-800/50 rounded-lg p-4">
                <p className="text-white font-medium">{selectedPurchase.guest_name}</p>
                <p className="text-neutral-400 text-sm">{selectedPurchase.package_name}</p>
                <p className="text-neutral-500 text-sm">
                  Current credits: {selectedPurchase.credits_remaining}/{selectedPurchase.credits_purchased}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-white">Quick select</Label>
              <Select
                value={creditInputValue}
                onValueChange={(v) => {
                  setCreditInputValue(v);
                  setCreditsToAdd(Number(v));
                }}
              >
                <SelectTrigger className="input-empire bg-neutral-900 border-neutral-700">
                  <SelectValue placeholder="Choose amount" />
                </SelectTrigger>
                <SelectContent>
                  {CREDIT_QUICK_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n >= 0 ? `+${n} credits` : `${n} credits (reduce)`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Or enter any number (-50 to +50)</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={creditInputValue}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || v === "-" || /^-?\d*$/.test(v)) {
                    setCreditInputValue(v);
                    const n = parseInt(v, 10);
                    if (!Number.isNaN(n)) setCreditsToAdd(n);
                  }
                }}
                className="input-empire"
                placeholder="e.g. -3 or 5"
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setAddCreditsDialogOpen(false)}
                className="flex-1 border-neutral-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddCredits}
                disabled={submitting}
                className="flex-1 btn-empire"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Credits"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
